<template>
    <div class="appointment_series_table">
        <div class="kgr-row kgr-row--smallMargin">
            <kgr-loading
                v-if="loading"
                inline
            />
            <kgr-appointment-series-table
                v-else
                :appointment-series-list="appointmentSeriesList"
                @remove_interval="removeAppointmentSeries"
                @edit_interval="editAppointmentSeries"
            />
            <kgr-form-row
                :error="error"
                field-name="appointmentSeriesList"
            />
        </div>
        <div v-show="showForm">
            <div
                v-if="isUsed"
                class="kgr-grid kgr-grid--gutters"
            >
                <div class="kgr-grid__cell">
                    <p class="kgr-color--warning">
                        {{ $t('Die Terminserie wird bereits verwendet und kann deshalb nicht mehr geändert werden. Nur das Enddatum des Intervals kann ggf. angepasst werden.') }}
                    </p>
                </div>
            </div>
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--gutters">
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Startdatum')"
                            :error="error"
                            field-name="startDate"
                            required
                        >
                            <kgr-date-picker
                                v-model.trim="startDate"
                                :disabled="isUsed"
                                @enter="onKeyUpEnter"
                                @post-input="setIntervalEndDate"
                            />
                        </kgr-form-row>
                    </div>
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Start')"
                            :error="error"
                            field-name="startTime"
                            required
                        >
                            <input
                                v-model.trim="startTime"
                                type="time"
                                class="kgr-input"
                                :disabled="isUsed"
                                @enter="onKeyUpEnter"
                                @input="checkForm()"
                            />
                        </kgr-form-row>
                    </div>
                </div>
            </div>
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--gutters">
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Enddatum')"
                            :error="error"
                            field-name="endDate"
                            required
                        >
                            <kgr-date-picker
                                v-model.trim="endDate"
                                :disabled="isUsed"
                                @enter="onKeyUpEnter"
                                @post-input="checkForm()"
                            />
                        </kgr-form-row>
                    </div>
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Ende')"
                            :error="error"
                            field-name="endTime"
                            required
                        >
                            <input
                                v-model.trim="endTime"
                                type="time"
                                class="kgr-input"
                                :disabled="isUsed"
                                @enter="onKeyUpEnter"
                                @input="checkForm()"
                            />
                        </kgr-form-row>
                    </div>
                </div>
            </div>
            <kgr-form-row
                :label="$t('offering.interval')"
                :error="error"
                field-name="interval"
                required
            >
                <kgr-generic-select
                    v-model="interval"
                    :options="intervals"
                    display="name"
                    :disabled="isUsed"
                />
            </kgr-form-row>

            <div
                v-if="activeBookingsUntil"
                class="kgr-grid kgr-grid--gutters"
            >
                <div class="kgr-grid__cell">
                    <p class="kgr-color--warning">
                        {{ $t('Die Terminserie kann frühstens am {until} beendet werden, da es aktive Buchungen gibt. Storniere die Buchungen oder sage die Termine ab, um die Terminserie früher zu beenden.', {until: activeBookingsUntil.toLocaleDateString() }) }}
                    </p>
                </div>
            </div>
            <kgr-form-row
                v-show="interval && interval.value !== null"
                :label="$t('offering.intervalEndDate')"
                :error="error"
                field-name="intervalEndDate"
            >
                <kgr-date-picker
                    v-model.trim="intervalEndDate"
                    :min-date="minIntervalEndDate"
                    @enter="onKeyUpEnter"
                    @post-input="checkForm()"
                />
            </kgr-form-row>
            <br />
            <button
                class="kgr-button"
                @click="clearForm"
            >
                {{ $t('offering.cancel') }}
            </button>
            <kgr-button
                v-if="!inEditMode"
                :value="$t('offering.btnAddInterval')"
                :pending="pending"
                :disabled="disableSave"
                @click="addAppointmentSeries"
            />
            <kgr-button
                v-else
                :value="$t('offering.btnEditInterval')"
                :pending="pending"
                :disabled="disableSave"
                @click="addAppointmentSeries"
            />
        </div>
        <div v-show="!showForm">
            <button
                class="kgr-button kgr-button--accent"
                @click="toggleShowForm"
            >
                {{ $t('offering.btnAddInterval') }}
            </button>
        </div>
    </div>
</template>

<script>
import AppointmentSeriesRepository from '../../js/repository/AppointmentSeriesRepository';
import DatePicker from '../common/form/picker/DatePicker';
import Error from '../../js/model/Error';
import FormRow from '../common/form/FormRow';
import GenericSelect from '../common/form/select/GenericSelect';
import KgrAppointmentSeriesTable from './KgrAppointmentSeriesTable';
import KgrButton from '../common/form/button/KgrButton.vue';
import Loading from '../common/Loading';
import Offering from '../../js/dto/offering/Offering';
import moment from 'moment/moment';

export default {
    name: 'KgrOfferingEditAppointmentSeriesComponent',
    components: {
        'kgr-appointment-series-table': KgrAppointmentSeriesTable,
        'kgr-button': KgrButton,
        'kgr-date-picker': DatePicker,
        'kgr-form-row': FormRow,
        'kgr-generic-select': GenericSelect,
        'kgr-loading': Loading,
    },
    props: {
        offering: {
            type: Offering,
            required: true,
        },
        saveAction: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            pending: false,
            disableSave: false,
            appointmentSeriesList: [],
            showForm: false,
            inEditMode: false,
            appointmentSeriesId: null,
            startDate: '',
            startTime: '',
            endDate: '',
            endTime: '',
            interval: null,
            intervalEndDate: '',
            showInterval: false,
            isUsed: false,
            activeBookingsUntil: null,
            intervals: [
                {'name': this.$t('interval.weekly'), 'value': 'weekly'},
                {'name': this.$t('interval.biweekly'), 'value': 'biweekly'},
                {'name': this.$t('interval.daily'), 'value': 'daily'},
                {'name': this.$t('interval.once'), 'value': null},
            ],
            weekdays: [
                {name: this.$t('weekday_1'), value: 1},
                {name: this.$t('weekday_2'), value: 2},
                {name: this.$t('weekday_3'), value: 3},
                {name: this.$t('weekday_4'), value: 4},
                {name: this.$t('weekday_5'), value: 5},
                {name: this.$t('weekday_6'), value: 6},
                {name: this.$t('weekday_7'), value: 7},
            ],
        }
    },
    computed: {
        isEmptyForm() {
            return !this.showForm
                || ('' === this.startDate || null === this.endDate)
                && ('' === this.startTime || null === this.startTime)
                && ('' === this.endDate || null === this.endDate)
                && ('' === this.endTime || null === this.endDate)
                && ('' === this.interval || null === this.interval)
                && ('' === this.intervalEndDate || null === this.intervalEndDate);

        },
        minIntervalEndDate() {
            if (!this.startDate) {
                return this.activeBookingsUntil;
            }
            const dayAfterStart = moment(this.startDate, 'DD.MM.YYYY').add(1, 'days').toDate();
            if (this.activeBookingsUntil && this.activeBookingsUntil > dayAfterStart) {
                return this.activeBookingsUntil;
            }
            return dayAfterStart;
        },
    },
    watch: {
        saveAction() {
            if (this.saveAction && this.showForm) {
                this.addAppointmentSeries();
            }
        },
        interval(newInterval) {
            if (!newInterval || newInterval.value === null) {
                this.intervalEndDate = null;
            } else if (!this.intervalEndDate && this.minIntervalEndDate) {
                this.intervalEndDate = moment(this.minIntervalEndDate).format('DD.MM.YYYY');
            }
        },
    },
    created() {
        AppointmentSeriesRepository.findAll(this.offering)
            .then(appointmentSeriesList => {
                this.appointmentSeriesList = appointmentSeriesList;
                this.loading = false;
            })
            .catch(() => this.$notify.error('error.loadingData'));
    },
    methods: {
        editAppointmentSeries: function (appointmentSeries) {
            this.clearForm();
            this.showForm = true;
            this.inEditMode = true;
            this.isUsed = appointmentSeries.isUsed;
            this.appointmentSeriesId = appointmentSeries.id;
            this.interval = this.getInterval(appointmentSeries.interval);
            this.weekday = this.getWeekday(appointmentSeries.weekday);
            this.startTime = moment(appointmentSeries.start, 'YYYY-MM-DD HH:mm').format('HH:mm');
            this.endTime = moment(appointmentSeries.end, 'YYYY-MM-DD HH:mm').format('HH:mm');
            this.startDate = moment(appointmentSeries.start, 'YYYY-MM-DD HH:mm').format('DD.MM.YYYY');
            this.endDate = moment(appointmentSeries.end, 'YYYY-MM-DD HH:mm').format('DD.MM.YYYY');
            if (appointmentSeries.activeBookingsUntil) {
                this.activeBookingsUntil = new Date(appointmentSeries.activeBookingsUntil);
            }
            if (appointmentSeries.intervalEnd) {
                this.intervalEndDate = moment(appointmentSeries.intervalEnd, 'YYYY-MM-DD').format('DD.MM.YYYY');
            }
        },
        removeAppointmentSeries: function (appointmentSeries) {
            showConfirm(this.$t('confirm'), this.$t('Soll die Terminserie wirklich gelöscht werden? Die Änderung wird sofort angewendet.'), () => {
                AppointmentSeriesRepository.delete(appointmentSeries)
                    .then(() => {
                        this.appointmentSeriesList = this.appointmentSeriesList.filter(item => item !== appointmentSeries);
                        this.$notify.success('saveChanges.success');
                        this.$emit('appointmentSeriesListChanged', this.appointmentSeriesList);
                    })
                    .catch(() => this.$notify.error('error.loadingData'));
            }, 'Ok');
        },
        toggleShowForm() {
            this.showForm = !this.showForm;
        },
        clearForm() {
            this.error = new Error();
            this.disableSave = false;
            this.$emit('appointmentInvalid', false);
            this.interval = null;
            this.startDate = null;
            this.startTime = null;
            this.endDate = null;
            this.endTime = null;
            this.weekday = null;
            this.activeBookingsUntil = null;
            this.isUsed = false;
            this.showInterval = false;
            this.appointmentSeriesId = null;
            this.showForm = false;
            this.inEditMode = false;
        },
        addAppointmentSeries() {
            this.checkForm();
            if (this.error.details.length !== 0 || this.isEmptyForm) {
                return;
            }

            let appointmentSeries = {
                id: this.appointmentSeriesId,
                interval: this.interval && this.interval.value ? {
                    type: this.interval.value,
                    weekday: moment(this.startDate + ' ' + this.startTime, 'DD.MM.YYYY HH:mm').isoWeekday(),
                    date: moment(this.startDate + ' ' + this.startTime, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD'),
                    day: parseInt(moment(this.startDate + ' ' + this.startTime, 'DD.MM.YYYY HH:mm').format('DD')),
                } : null,
                weekday: moment(this.startDate + ' ' + this.startTime, 'DD.MM.YYYY HH:mm').isoWeekday(),
                start: moment(this.startDate + ' ' + this.startTime, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD HH:mm'),
                end: moment(this.endDate + ' ' + this.endTime, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD HH:mm'),
                intervalEnd: this.intervalEndDate ? moment(this.intervalEndDate, 'DD.MM.YYYY').format('YYYY-MM-DD') : null,
                isUsed: this.isUsed,
                activeBookingsUntil: this.activeBookingsUntil,
            };

            this.pending = true;
            if (appointmentSeries.id === null) {
                AppointmentSeriesRepository.add(appointmentSeries, this.offering)
                    .then(id => {
                        appointmentSeries.id = id;
                        this.appointmentSeriesList.push(appointmentSeries);
                        this.$notify.success('saveChanges.success');
                        this.clearForm();
                        this.$emit('appointmentSeriesListChanged', this.appointmentSeriesList);
                    })
                    .catch(() => this.$notify.error('error.loadingData'))
                    .finally(() => this.pending = false);
            } else {
                AppointmentSeriesRepository.update(appointmentSeries)
                    .then(() => {
                        this.$notify.success('saveChanges.success');
                        this.appointmentSeriesList = this.appointmentSeriesList.filter(element => element.id !== this.appointmentSeriesId);
                        this.appointmentSeriesList.push(appointmentSeries);
                        this.clearForm();
                        this.$emit('appointmentSeriesListChanged', this.appointmentSeriesList);
                    })
                    .catch(() => this.$notify.error('error.loadingData'))
                    .finally(() => this.pending = false);
            }
        },
        setIntervalEndDate() {
            if (!this.endDate) {
                this.endDate = this.startDate;
            }
            this.checkForm();
        },
        getInterval(intervalValue) {
            if (intervalValue) {
                return this.intervals.find(interval => interval.value === intervalValue.type);
            }
            return null;
        },
        onKeyUpEnter() {
            this.$emit('enter');
        },
        getWeekday(weekdayValue) {
            return this.weekdays.find(weekday => weekday.value === weekdayValue);
        },
        checkForm() {
            this.error = new Error();
            this.error.details = [];
            if (!this.showForm || this.saveAction && this.isEmptyForm) {
                return;
            }
            if (!this.startDate) {
                this.error.details.push({
                    'attributeName': 'startDate',
                    'constraintMessage': this.$t('offering.error.chooseStartDate'),
                });
            }
            if (!this.endDate) {
                this.error.details.push({
                    'attributeName': 'endDate',
                    'constraintMessage': this.$t('offering.error.chooseEndDate'),
                });
            }
            if (!this.startTime) {
                this.error.details.push({
                    'attributeName': 'startTime',
                    'constraintMessage': this.$t('offering.error.chooseStartTime'),
                });
            }
            if (!this.endTime) {
                this.error.details.push({
                    'attributeName': 'endTime',
                    'constraintMessage': this.$t('offering.error.chooseEndTime'),
                });
            }
            if (moment(this.endDate + ' ' + this.endTime, 'DD.MM.YYYY HH:mm').diff(moment(this.startDate + ' ' + this.startTime, 'DD.MM.YYYY HH:mm'), 'minutes') < 0) {
                this.error.details.push({
                    'attributeName': 'startDate',
                    'constraintMessage': this.$t('offering.error.EndTimeGreaterStartTime'),
                });
            }
            if (this.intervalEndDate && this.startDate && moment(this.intervalEndDate, 'DD.MM.YYYY').isSameOrBefore(moment(this.startDate, 'DD.MM.YYYY'), 'day')) {
                this.error.details.push({
                    'attributeName': 'intervalEndDate',
                    'constraintMessage': this.$t('offering.error.IntervalEndBeforeStartDate'),
                });
            }
            if (this.interval
                && this.interval.value
                && !this.intervalEndDate
                && this.offering.isBookingModeOffering()
            ) {
                this.error.details.push({
                    'attributeName': 'intervalEndDate',
                    'constraintMessage': this.$t('offering.error.noIntervalEndDateAndBookingModeOffering'),
                });
            }
            if (this.error.details.length !== 0) {
                this.disableSave = true;
            } else {
                this.disableSave = false;
            }
            this.$emit('appointmentInvalid', this.disableSave)
        },
    },
}
</script>
